import { ConvexError } from "convex/values";
import toast, { type ToastOptions } from "react-hot-toast";
import { reportError } from "@/lib/analytics";
import i18n from "@/i18n";
import { isAppErrorData } from "@/../convex/config/errorsConfig";

/**
 * Messages de transport, qui disent comment ça a cassé mais rien de ce que
 * l'utilisateur essayait de faire : diagnostic d'une fonction Convex
 * (`[CONVEX M(nodes:patch)] [Request ID: …] Server Error`), connexion perdue,
 * `fetch` ou axios en échec réseau ou HTTP.
 */
const TECHNICAL_MESSAGES = [
  /^\[CONVEX /,
  /Connection lost while action was in flight/,
  /^Failed to fetch$/,
  /^NetworkError when attempting to fetch resource/,
  /^Load failed$/,
  /^Network Error$/,
  /^Request failed with status code \d+$/,
  /^timeout of \d+ms exceeded$/,
];

/**
 * Le message lisible d'une erreur, ou `fallback` quand elle n'en porte pas.
 *
 * - Une `ConvexError` levée par `appError` porte `{ code, message, params }`
 *   (cf. `convex/config/errorsConfig.ts`) : on traduit `code` dans la langue
 *   de l'interface (src/locales/<lang>/errors.json) en réinjectant `params`,
 *   et on retombe sur la phrase anglaise `message` si la traduction manque.
 * - Une `ConvexError` dont `.data` est une chaîne (composants tiers, code pas
 *   encore migré) l'affiche telle quelle.
 * - Une erreur de transport (cf. `TECHNICAL_MESSAGES`) n'a rien à faire sous
 *   les yeux de l'utilisateur : le texte de repli dit mieux ce qui a échoué.
 * - Une erreur locale (micro refusé, CSV illisible, type de fichier) dit déjà
 *   ce qui s'est passé.
 */
export function getErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof ConvexError) {
    const data: unknown = error.data;
    if (isAppErrorData(data)) {
      const key = `errors:${data.code}`;
      return i18n.exists(key)
        ? // Clé construite à l'exécution : le typage strict des clés ne peut
          // pas la vérifier, le test des locales s'en charge.
          i18n.t(key as never, data.params ?? {})
        : data.message;
    }
    return typeof data === "string" && data ? data : fallback;
  }
  if (
    error instanceof Error &&
    error.message &&
    !TECHNICAL_MESSAGES.some((pattern) => pattern.test(error.message))
  ) {
    return error.message;
  }
  return fallback;
}

export function toastError(
  error: unknown,
  defaultMessage: string,
  options?: ToastOptions,
) {
  // `reportError` logge en console et remonte à PostHog : c'est le seul chemin
  // de capture de l'app (cf. src/lib/analytics.ts). Une `ConvexError` est le
  // plus souvent un refus voulu par le backend (« ces deux nodes sont déjà
  // reliés ») ; certaines trahissent pourtant un bug client (document
  // BlockNote ou ligne de table invalide). On les remonte toutes, marquées
  // `expected`, pour pouvoir les filtrer dans PostHog sans perdre le signal.
  reportError(error, {
    source: "toastError",
    defaultMessage,
    expected: error instanceof ConvexError,
  });
  toast.error(getErrorMessage(error, defaultMessage), options);
}
